/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description fetch full listing/building when initiating the message flow
 */

import { selectIsOnline } from '@zcode-ui/web-zumper-global-state'
import { isCommonAuthError } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import { warnOffline } from '@zcode/nr/es/shared/modules/app/thunks'
import type { Building, Listing, NormalListingData } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { logger } from '../../../../logger'
import type { GetState } from '../../../types'
import {
  cancelFetchingEntity,
  doneFetchingEntity,
  errorFetchingEntity,
  receiveEntity,
  startFetchingEntity,
} from '../../actions/messageModal'
import { selectEntity } from '../../selectors/messageModal/index'
import { selectCanceled, selectFetchingKey } from '../../selectors/messageModal/meta'

interface BuildingIdParams {
  buildingId: number
  listingId?: number | undefined
}
interface ListingIdParams {
  buildingId?: undefined
  listingId: number
}

function fetchMessagingEntity<Params extends BuildingIdParams | ListingIdParams>({
  buildingId,
  listingId,
}: Params) {
  return (
    dispatch: Dispatch,
    getState: GetState
  ):
    | undefined
    | Promise<
        | undefined
        | null
        | (Params extends BuildingIdParams ? Building : never)
        | (Params extends ListingIdParams ? Listing<NormalListingData> : never)
      > => {
    const state = getState()
    const isOnline = selectIsOnline(state)
    if (!isOnline) {
      dispatch(warnOffline())
      return
    }
    let promise: ReturnType<typeof Api.buildings.get> | ReturnType<typeof Api.listings.get>
    let key: number

    if (typeof buildingId === 'number') {
      key = buildingId
      promise = Api.buildings.get({ buildingId })
    } else {
      key = listingId
      promise = Api.listings.get({ listingId })
    }

    const currentFetchingKey = selectFetchingKey(getState())

    if (currentFetchingKey !== null && currentFetchingKey !== key) {
      dispatch(cancelFetchingEntity())
    } else if (currentFetchingKey === key) {
      // @ts-expect-error might be a Listing when we need a Building or vice-versa
      return Promise.resolve(selectEntity(getState()))
    }

    dispatch(startFetchingEntity(key))

    // @ts-expect-error might be a Listing when we need a Building or vice-versa
    return promise
      .then((entity) => {
        const canceled = selectCanceled(getState())

        if (!canceled) {
          dispatch(receiveEntity(entity))
        }

        dispatch(doneFetchingEntity())
        return entity
      })
      .catch((error: unknown) => {
        const canceled = selectCanceled(getState())

        if (!canceled) {
          dispatch(errorFetchingEntity(error))
        }

        dispatch(doneFetchingEntity())
        if (!isCommonAuthError(error)) {
          logger.error('error loading entity in fetchMessagingEntity:', error)
        }
        return undefined
      })
  }
}

export default fetchMessagingEntity
